<template>
  <div class="payment-info card visible">
    <fieldset>
      <label>
        <div id="card-element" class="field"></div>
      </label>
    </fieldset>
    <button
      type="submit"
      :disabled="disableButton"
      @click="onPaymentButtonClick"
    >
      {{ buttonText }}
    </button>
    <div
      id="card-errors"
      class="element-errors"
      :class="{ visible: paymentErrorMessage }"
    >
      {{ paymentErrorMessage }}
    </div>
  </div>
</template>

<script>
import { validateOrder } from "../../api";

export default {
  name: "StripeCreditCardInput",
  data() {
    return {
      stripe: null,
      elements: null,
      card: null,
      disableButton: false,
      paymentErrorMessage: null
    };
  },
  props: {
    paymentIntentClientSecret: {
      type: String,
      required: true
    },
    buttonDisplayText: {
      type: String,
      required: true
    },
    billingAddress: {
      type: Object,
      required: true
      // same object from checkout, e.g:
      // address_city:
      // address_country:
      // address_line_1:
      // address_line_2:
      // address_state:
      // address_zip:
      // email:
      // name:
    },
    orderId: {
      type: String,
      required: true
    },
    stripeAccountId: {
      validator: prop => typeof prop === "string" || prop === null,
      required: false
    },
  },
  mounted() {
    // eslint-disable-next-line no-undef
    let stripeOptions = {};
    // this is for new storefronts which support Stripe Connect
    // legacy storefronts being phased out won't include this option
    if (this.stripeAccountId) {
      stripeOptions = {
        stripeAccount: this.stripeAccountId
      }
    }
    this.stripe = Stripe(process.env.VUE_APP_STRIPE_API_PK, stripeOptions);
    this.elements = this.stripe.elements();
    const style = {
      base: {
        iconColor: "#666ee8",
        color: "#31325f",
        fontWeight: 400,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "15px",
        "::placeholder": {
          color: "#aab7c4"
        },
        ":-webkit-autofill": {
          color: "#666ee8"
        }
      }
    };
    // Create a Card Element and pass the custom styles to it.
    this.card = this.elements.create("card", { style });
    // Mount the Card Element on the page.
    this.card.mount("#card-element");
    // Monitor change events on the Card Element to display any errors.
    this.card.on("change", ({ error }) => {
      this.paymentErrorMessage = null;
      if (error) {
        this.paymentErrorMessage = error.message;
      }
      // Re-enable the Pay button.
      this.disableButton = false;
    });
  },
  computed: {
    buttonText() {
      if (this.disableButton) {
        return `Processing...`;
      } else {
        return this.buttonDisplayText;
      }
    }
  },
  methods: {
    onPaymentButtonClick: async function() {
      if (this.disableButton) return;
      // notify the parent, so they can block the UI
      this.$emit("evt-payment-request-start");
      // disable the checkout button
      this.disableButton = true;
      // reset the payment error message
      this.paymentErrorMessage = null;
      // before we try and process the payment, perform one last validation of the order
      // this checks that the product items are still for sale and not out of stock
      const bIsValid = await this.validateOrder();
      // if the order is not valid, emit an event to the parent, so they can tear down checkout
      // from this component's perspective, we bail too
      if (bIsValid === false) {
        this.$emit("evt-order-pre-payment-validation-failure");
        return;
      }
      // construct the billing info object
      const billingDetails = {
        address: {
          city: this.billingAddress.address_city,
          country: this.billingAddress.address_country,
          line1: this.billingAddress.address_line_1,
          line2: this.billingAddress.address_line_2,
          postal_code: this.billingAddress.address_zip,
          state: this.billingAddress.address_state
        },
        email: this.billingAddress.email,
        name: this.billingAddress.name
      };
      try {
        // Try charging the card
        // Note: The Stripe SDK may at this point launch a secondary UI
        // for 3D secure, if required.
        // This is presented as a popup over the entire UI
        const response = await this.stripe.handleCardPayment(
          this.paymentIntentClientSecret,
          this.card,
          {
            payment_method_data: {
              billing_details: billingDetails
            }
          }
        );
        this.handlePayment(response);
      } catch (e) {
        console.error("Uncaught exception calling Stripe.handleCardPayment()");
        console.error(e);
        this.paymentErrorMessage =
          "Sorry, your card could not be charged. Please try again";
        this.disableButton = false;
      } finally {
        // notify the parent so they can unblock the UI
        this.$emit("evt-payment-request-finish");
      }
    },
    handlePayment(paymentResponse) {
      const { paymentIntent, error } = paymentResponse;
      if (error) {
        // display a meaningful error message to the user
        this.paymentErrorMessage = error.message;
        this.disableButton = false;
      } else if (paymentIntent.status === "succeeded") {
        // Success! payment is confirmed
        this.$emit("evt-payment-success", "succeeded");
      } else if (paymentIntent.status === "processing") {
        // Success! payment is still processing
        this.$emit("evt-payment-success", "processing");
      } else {
        // payment has failed.
        this.paymentErrorMessage =
          "Sorry, your card could not be charged. Please try again";
        this.disableButton = false;
      }
    },
    validateOrder: async function() {
      try {
        await validateOrder(this.orderId);
      } catch (e) {
        return false;
      }
      return true;
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: none;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
  font-size: 15px;
  line-height: 1.4em;
  color: #525f7f;
  text-align: center;
}

/* Form */

fieldset {
  margin-bottom: 20px;
  background: #fff;
  box-shadow: 0 1px 3px 0 rgba(50, 50, 93, 0.15),
    0 4px 6px 0 rgba(112, 157, 199, 0.15);
  border-radius: 4px;
  border: none;
  font-size: 0;
}

fieldset label {
  position: relative;
  display: flex;
  flex-direction: row;
  height: 42px;
  padding: 10px 0;
  align-items: center;
  justify-content: center;
  color: #8898aa;
  font-weight: 400;
}

fieldset label:not(:last-child) {
  border-bottom: 1px solid #f0f5fa;
}

fieldset label span {
  min-width: 125px;
  padding: 0 15px;
  text-align: right;
}

fieldset .redirect label span {
  width: 100%;
  text-align: center;
}

.field {
  flex: 1;
  padding: 0 15px;
  background: transparent;
  font-weight: 400;
  color: #31325f;
  outline: none;
  cursor: text;
}

.field::-webkit-input-placeholder {
  color: #aab7c4;
}
.field::-moz-placeholder {
  color: #aab7c4;
}
.field:-ms-input-placeholder {
  color: #aab7c4;
}

input {
  flex: 1;
  border-style: none;
  outline: none;
  color: #313b3f;
}

select:focus {
  color: #666ee8;
}

::-webkit-input-placeholder {
  color: #cfd7e0;
}
::-moz-placeholder {
  color: #cfd7e0;
  opacity: 1;
}
:-ms-input-placeholder {
  color: #cfd7e0;
}

input:-webkit-autofill,
select:-webkit-autofill {
  -webkit-text-fill-color: #666ee8;
  transition: background-color 100000000s;
  -webkit-animation: 1ms void-animation-out 1s;
}

.StripeElement--webkit-autofill {
  background: transparent !important;
}

#card-element {
  margin-top: -1px;
}

button {
  display: block;
  background: #666ee8;
  color: #fff;
  box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  border: 0;
  font-weight: 700;
  width: 100%;
  height: 40px;
  outline: none;
  cursor: pointer;
  transition: all 0.15s ease;
}

button:focus {
  background: #555abf;
}

button:hover {
  transform: translateY(-1px);
  box-shadow: 0 7px 14px 0 rgba(50, 50, 93, 0.1),
    0 3px 6px 0 rgba(0, 0, 0, 0.08);
}

button:active {
  background: #43458b;
}

.element-errors {
  display: inline-flex;
  height: 20px;
  margin: 15px auto 0;
  padding-left: 20px;
  color: #e25950;
  opacity: 0;
  transform: translateY(10px);
  transition-property: opacity, transform;
  transition-duration: 0.35s;
  transition-timing-function: cubic-bezier(0.165, 0.84, 0.44, 1);
  background: url(../../../../assets/images/stripe_error.svg) center left
    no-repeat;
  background-size: 15px 15px;
}

.element-errors.visible {
  opacity: 1;
  transform: none;
}
</style>
